// Dialog para que cualquier usuario autenticado mande un comentario sobre el
// sistema durante la beta. Categoría + mensaje + screenshot opcional (adjuntar
// archivo o pegar con Ctrl+V).

import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
    TextField, FormControl, RadioGroup, Radio, FormControlLabel,
    Typography, Alert, Stack, IconButton, CircularProgress,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ChatIcon from '@mui/icons-material/Chat';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { feedbackBetaCrearJWT } from '../api/endpoints';

const PRIMARY = '#1769aa';
const BR = 4;

export default function FeedbackBetaDialog({ open, onClose, onSuccess }) {
    const [categoria, setCategoria] = useState('bug');
    const [mensaje, setMensaje] = useState('');
    const [screenshot, setScreenshot] = useState(null);     // File | Blob
    const [previewUrl, setPreviewUrl] = useState(null);
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState('');
    const [exito, setExito] = useState(false);
    const fileInputRef = useRef(null);
    const dialogRef = useRef(null);

    // Reset al abrir/cerrar
    useEffect(() => {
        if (open) {
            setCategoria('bug');
            setMensaje('');
            setError('');
            setExito(false);
            limpiarScreenshot();
        }
        // eslint-disable-next-line
    }, [open]);

    // Cleanup blob URL al desmontar / cambiar
    useEffect(() => {
        return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    }, [previewUrl]);

    // Pegar imagen desde clipboard (Ctrl+V mientras el dialog está abierto)
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            for (const item of items) {
                if (item.type && item.type.startsWith('image/')) {
                    const blob = item.getAsFile();
                    if (blob) {
                        elegirScreenshot(blob);
                        e.preventDefault();
                        return;
                    }
                }
            }
        };
        window.addEventListener('paste', handler);
        return () => window.removeEventListener('paste', handler);
        // elegirScreenshot se recrea cada render; agregarlo re-registraría el listener constantemente
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const limpiarScreenshot = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setScreenshot(null);
        setPreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const elegirScreenshot = (file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            setError('La imagen no puede superar 5 MB.');
            return;
        }
        if (!file.type?.startsWith('image/')) {
            setError('El archivo debe ser una imagen.');
            return;
        }
        setError('');
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setScreenshot(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const enviar = async () => {
        const msg = mensaje.trim();
        if (!msg) { setError('Escribí qué querés reportar.'); return; }
        if (msg.length < 5) { setError('Decinos un poco más, al menos 5 caracteres.'); return; }
        setEnviando(true); setError('');
        try {
            const r = await feedbackBetaCrearJWT({ categoria, mensaje: msg, screenshot });
            if (r?.ok) {
                setExito(true);
                setTimeout(() => {
                    onSuccess && onSuccess();
                    onClose && onClose();
                }, 1200);
            } else {
                setError(r?.mensaje || 'Error al enviar');
            }
        } catch (e) {
            setError(e?.message || 'Error al enviar');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: '4px' } }} ref={dialogRef}>
            <DialogTitle sx={{ color: PRIMARY, fontWeight: 700 }}>
                Reportar comentario
                <Typography variant="caption" sx={{ display: 'block', color: '#777', fontWeight: 400 }}>
                    Estamos en período de prueba — contanos qué viste, qué falla o qué se podría mejorar.
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {exito ? (
                    <Alert severity="success" sx={{ mb: 1 }}>
                        ¡Gracias! Tu comentario llegó. Si querés, podés cerrar esta ventana y seguir trabajando.
                    </Alert>
                ) : (
                    <>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', textTransform: 'uppercase' }}>
                            ¿De qué se trata?
                        </Typography>
                        <FormControl sx={{ mt: 0.5, mb: 2 }}>
                            <RadioGroup row value={categoria} onChange={e => setCategoria(e.target.value)}>
                                <FormControlLabel value="bug" control={<Radio size="small" />}
                                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <BugReportIcon fontSize="small" sx={{ color: '#c62828' }} /> Algo falla
                                    </Box>} />
                                <FormControlLabel value="mejora" control={<Radio size="small" />}
                                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <LightbulbIcon fontSize="small" sx={{ color: '#f57c00' }} /> Sugerencia
                                    </Box>} />
                                <FormControlLabel value="otro" control={<Radio size="small" />}
                                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <ChatIcon fontSize="small" sx={{ color: '#1769aa' }} /> Otro
                                    </Box>} />
                            </RadioGroup>
                        </FormControl>

                        <TextField
                            label="Comentario"
                            multiline minRows={5} maxRows={12} fullWidth
                            value={mensaje}
                            onChange={e => setMensaje(e.target.value)}
                            placeholder="Describí el problema o la idea con tus palabras…"
                            sx={{ mb: 2 }}
                            inputProps={{ maxLength: 5000 }}
                            helperText={`${mensaje.length} / 5000`}
                        />

                        <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                Captura de pantalla (opcional)
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                                Hacé Print Screen y pegá con Ctrl+V, o adjuntá un archivo de imagen.
                            </Typography>
                            {previewUrl ? (
                                <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                                    <img src={previewUrl} alt="preview"
                                        style={{ maxWidth: '100%', maxHeight: 240, borderRadius: BR, border: '1px solid #ddd' }} />
                                    <IconButton size="small" onClick={limpiarScreenshot}
                                        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.9)',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                                        <DeleteIcon fontSize="small" sx={{ color: '#c62828' }} />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Stack direction="row" spacing={1}>
                                    <Button variant="outlined" size="small" startIcon={<AttachFileIcon />}
                                        component="label"
                                        sx={{ textTransform: 'none', borderRadius: BR }}>
                                        Adjuntar imagen
                                        <input ref={fileInputRef} hidden type="file" accept="image/*"
                                            onChange={e => elegirScreenshot(e.target.files?.[0])} />
                                    </Button>
                                </Stack>
                            )}
                        </Box>

                        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {!exito && <>
                    <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
                    <Button variant="contained" onClick={enviar} disabled={enviando || !mensaje.trim()}
                        sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0d4f86' }, textTransform: 'none' }}>
                        {enviando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Enviar'}
                    </Button>
                </>}
                {exito && <Button onClick={onClose} variant="contained" sx={{ bgcolor: PRIMARY, textTransform: 'none' }}>
                    Cerrar
                </Button>}
            </DialogActions>
        </Dialog>
    );
}
